import { renderAlert } from "../../../CommonComponents/Alert/Alert";
import { getFlatChildrenColumnOfTimeSeries, isStringifiedArrayWithPropName, isTheGroupTimeseries } from "../../../helpers/ExportHelper";
import xTablesStore from "../redux/xTablesStore";
import { v4 as uuidv4 } from 'uuid';
import { updateValues } from "./dataHarvestHelper";
import { updateCellValue } from "./xTablesHelper";
import { timeSeriesChildColumnListData } from "../cellComponents/FushionColumn/constants/MdvConstant";
import { deepCopy } from "./helpersSubModule";

// Define the types for your parameters
type FocusedCell = {
  column?: {
    colDef?: {
      listData?: string;
    };
  };
};

type Params = {
  oldValue?: string;
};

type RowData = any; // Adjust based on your actual row data structure
type GridRef = any;  // Adjust based on your actual grid reference structure

interface ListDataItem {
  propName?: string;
  [key: string]: any;
}

export const ImplementCopyPasteForMdv = (
  value: string,
  listDataMdv: string
): string => {
  // Assuming listData and newValue are JSON strings
  // let listDataStr :any =
  //   params.oldValue && isStringifiedArrayWithPropName(params.oldValue)
  //     ? listDataMdv
  //     : listDataMdv;
  let listDataStr :any = listDataMdv;
  let newValueStr = value;
  // Parse JSON strings into JavaScript arrays
  let listData: ListDataItem[] = 
    listDataStr && Array.isArray(listDataStr)
      ? listDataStr as unknown as ListDataItem[] // Handle edge case
      : isStringifiedArrayWithPropName(listDataStr) ? JSON.parse(listDataStr) : [];
      let newValue: ListDataItem[];

      // Check if newValueStr is valid JSON
      try {
          const parsedValue = JSON.parse(newValueStr);
          newValue = Array.isArray(parsedValue) ? parsedValue : JSON.parse("[]");
      } catch (error) {
          console.warn("Failed to parse JSON in value:", error);
          // If newValueStr is not valid JSON, default to an empty array
          newValue = JSON.parse("[]");
      }
  // Create a map from newValue for quick lookup
  let newValueMap = new Map<string, ListDataItem>();
  for (let item of newValue) {
    if (item.propName) {
      newValueMap.set(item.propName, item);
    }
  }
  let updatedNewValueItems: Array<any> = []; 
  for (let i = 0; i < listData.length; i++) {
    let listDataItem = listData[i];
    if (listDataItem.propName && newValueMap.has(listDataItem.propName)) {
      let newValueItem = newValueMap.get(listDataItem.propName);
      updatedNewValueItems.push(newValueItem); // Update listDataItem with newValueItem
      for (let key in newValueItem) {
        if (newValueItem.hasOwnProperty(key)) {
          listDataItem[key] = newValueItem[key];
        }
      }
    }
  }
  // Convert listData back to JSON string if needed
  let updatedListDataStr = JSON.stringify(updatedNewValueItems);

  return updatedListDataStr;
};

export interface ParsedData {
  propName: string;
  propAbbrevation: string;
  propType: string;
  propValue: any;
  propBgColor: string;
  propFgColor: string;
  rollup ?: string
}

export const parseDataString = (dataString: string): ParsedData[] => {
  const regex = /([\w\s]+)\s\[(\w+)\]\s\[(\w+-?\w*)\]:\s(\([^)]+\)|[^,]+)/g;
  const result: ParsedData[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(dataString)) !== null) {
    const [_, propName, propAbbrevation, propType, propValue] = match;
    const { propBgColor, propFgColor } = getPropColors(propName.trim(), propAbbrevation);

    let formattedValue: string | string[] = propValue.trim();

    if (propType === "multi-list" && formattedValue.startsWith("(") && formattedValue.endsWith(")")) {
      formattedValue = formattedValue.slice(1, -1).split(",").map(item => item.trim());
    }

    result.push({
      propName: propName.trim(),
      propAbbrevation: propAbbrevation,
      propType: propType,
      propValue: formattedValue,
      propBgColor: propBgColor,
      propFgColor: propFgColor
    });
  }
  return result;
};

interface PropColors {
  propBgColor: string;
  propFgColor: string;
}

function getPropColors(propName: string, propAbbrevation: string): PropColors {
  const colorMap: Record<string, Record<string, PropColors>> = {
    "Planned Time": {
      "PT": {
        propBgColor: "#B9DDFC",
        propFgColor: "none"
      }
    },
    "Actual Time": {
      "AT": {
        propBgColor: "#92D050",
        propFgColor: "none"
      }
    },
    "Reason": {
      "R": {
        propBgColor: "#FF7F50",
        propFgColor: "none"
      }
    },
    "Impediment": {
      "I": {
        propBgColor: "#FFFF00",
        propFgColor: "none"
      }
    },
    "Dependency": {
      "D": {
        propBgColor: "#FFC000",
        propFgColor: "none"
      }
    },
    "TaskEnded": {
      "TE": {
        propBgColor: "#FFB6C1",
        propFgColor: "none"
      }
    }
  };

  if (colorMap[propName] && colorMap[propName][propAbbrevation]) {
    return colorMap[propName][propAbbrevation];
  }

  return {
    propBgColor: '',
    propFgColor: 'none'
  };
}

interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const validatePropAbbreviation = (abbreviation: string): ValidationResult => {
  const abbreviationRegex = /^[A-Za-z0-9]*$/; // Only letters and numbers
  const onlyNumbersRegex = /^[0-9]+$/; // Only numbers

  if (!abbreviationRegex.test(abbreviation)) {
    return { isValid: false, message: "Abbreviation can only contain letters and numbers" };
  }
  if (onlyNumbersRegex.test(abbreviation)) {
    return { isValid: false, message: "Abbreviation cannot consist of only numbers" };
  }
  return { isValid: true };
};


export const validatePropName = (name: string): ValidationResult => {
  const onlyNumbersRegex = /^[0-9]+$/; // Only numbers
  const onlySpecialCharsRegex = /^[^A-Za-z0-9]+$/; // Only special characters

  if (onlyNumbersRegex.test(name)) {
    return { isValid: false, message: "Property name cannot be only numbers" };
  }
  if (onlySpecialCharsRegex.test(name)) {
    return { isValid: false, message: "Property name cannot be only special characters" };
  }
  return { isValid: true };
};

interface MdvField {
  propName: string;
  propAbbrevation: string;
  propType: string;
  propList: any
}

export const validationForMdvColumnTypeCreation = (mdvValue: MdvField[]): boolean => {
  for (const field of mdvValue) {
    const abbrevValidation = validatePropAbbreviation(field.propAbbrevation);
    const nameValidation = validatePropName(field.propName);

    if (
      (field.propType === "multi-list" || field.propType === "single-list") &&
      (!field.propList || field.propList.length === 0)
    ) {
      renderAlert("List Items are missing", "error", 2000);
      return false;
    }
    if (field.propName.trim() === "" && field.propAbbrevation.trim() !== "") {
      renderAlert("Property name is missing", "error", 2000);
      return false;
    } else if (
      field.propName.trim() !== "" &&
      field.propAbbrevation.trim() === ""
    ) {
      renderAlert("Abbreviation is missing", "error", 2000);
      return false;
    } else if (!abbrevValidation.isValid) {
      renderAlert(abbrevValidation.message, "error", 2000);
      return false;
    } else if (!nameValidation.isValid) {
      renderAlert(nameValidation.message, "error", 2000);
      return false;
    }
  }
  return true;
};


interface ColDef {
  block: boolean;
}

export const validatePropValue = (
  newValue: string,
  type: 'number' | 'string', // Extend this as needed
  colDef: ColDef,
  data: any
): boolean => {
  if (type === 'number') {
    const regex = /^\-?\d*\.?\d{0,2}$/;
    const numericValue = Number(newValue);

    const isValidNumber =
      (!colDef.block && (newValue === '-' || !isNaN(numericValue))) ||
      (colDef.block && !isNaN(numericValue) && numericValue >= 0);

    if (newValue.includes('.') && newValue.split('.')[1].length > 2) {
      renderAlert('Decimal points cannot exceed two places.', 'warning', 2000);
      return false;
    }

    if (newValue && (!isValidNumber || !regex.test(newValue))) {
      renderAlert('Please insert a valid number.', 'warning', 2000);
      return false;
    }

    if (newValue && numericValue.toString().length > 15 && !colDef.block) {
      renderAlert('Number with a maximum of 15 digits is allowed.', 'warning', 2000);
      return false;
    }

    if (newValue && colDef.block && numericValue > 720 && timeSeriesChildColumnListData.some(
      (item) =>
        item.propName === data.propName && item.propAbbrevation === data.propAbbrevation
    )) {
      renderAlert('Time Exceeding 720 Hours Cannot Be Logged.', 'warning', 2000);
      return false;
    }
  }

  return true;
};

interface ExistingMdvData {
  gridRef: GridRef;
  columnDefs: any; // Can be refined further if needed
  params: any;
}

export const logicToUpdateExistingData = async (gridRef: GridRef, columnDefs: any, params: any) => {
  const newRowsMap = new Map<string, RowData>();
  const originalRows: RowData[] = [];

  const { loggedInUser } = xTablesStore.getState().gridReducer;
  const currentColDef = params?.colDef;

  let listData = Array.isArray(currentColDef.listData)
    ? currentColDef.listData
    : JSON.parse(currentColDef.listData || '[]');

  const updatedListData = listData?.map((data: any) => ({
    ...data,
    propId: data.propId || uuidv4(),
  }));

  currentColDef.listData = JSON.stringify(updatedListData);

  const getRowKey = (row: any) => row?.id?.toString() || JSON.stringify(row);
  const addedRowKeys = new Set();

  const shouldSkipRow = (row: any, colToEdit: string) => !row?.[colToEdit]?.value;

  const getUpdatedRow = (row: any, colToEdit: string, fieldForDisplay: string) => {
    const rowKey = getRowKey(row);
    if (shouldSkipRow(row, colToEdit)) return null;

    if (!addedRowKeys.has(rowKey)) {
      originalRows.push(deepCopy(row));
      addedRowKeys.add(rowKey);
    }

    const prevData = {
      ...params.data,
      [colToEdit]: {
        ...params?.data?.[colToEdit],
        value: params?.data?.[colToEdit]?.value,
      },
    };

    const newValue = updateValues(row?.[colToEdit]?.value, updatedListData, true);
    const updatedCellValue = updateCellValue(prevData, colToEdit, "value", newValue, loggedInUser);

    const existingRow = newRowsMap.get(rowKey) || { ...row };
    existingRow[colToEdit] = updatedCellValue;
    existingRow[fieldForDisplay] = updatedCellValue.value;

    return { rowKey, updatedRow: existingRow };
  };

  const processColumn = async (colToEdit: string, fieldForDisplay: string) => {
    const { newArray: shouldModify, validDataArray } = await findMissingPropIdEntries(gridRef, colToEdit);
    const checking = findInvalidRows(validDataArray, updatedListData, colToEdit, params, currentColDef, loggedInUser);
    const combinedRowsToModify = [...shouldModify, ...checking];

    for (const row of combinedRowsToModify) {
      const result = getUpdatedRow(row, colToEdit, fieldForDisplay);
      if (result) {
        newRowsMap.set(result.rowKey, result.updatedRow);
      }
    }
  };

  const processTimeSeriesColumns = async () => {
    const colDefs = gridRef.current.api.getColumnDefs();
    for (const col of colDefs) {
      if (!col.marryChildren || !isTheGroupTimeseries(col)) continue;

      const children = getFlatChildrenColumnOfTimeSeries(col.children);
      for (const childCol of children) {
        const colToEdit = childCol?.field?.endsWith('_es') ? childCol.field : childCol.field + '_es';
        await processColumn(colToEdit, childCol.field);
      }
    }
  };

  if (currentColDef.block === "timeseries") {
    await processTimeSeriesColumns();
  } else {
    const colToEdit = currentColDef?.field?.endsWith('_es') ? currentColDef.field : currentColDef.field + '_es';
    await processColumn(colToEdit, currentColDef.field);
  }

  const newRows = Array.from(newRowsMap.values());
  return { newRows, originalRows };
};

async function findMissingPropIdEntries(gridRef: any, colToEdit: string): Promise<{ newArray: any[], validDataArray: any[] }>  {
  let newArray: any[] = [];
  let validDataArray: any[] = [];
  gridRef?.current?.api?.forEachNode((node: any) => {
    const row = JSON.parse(JSON.stringify(node?.data));
    
      if (row[colToEdit] && typeof row[colToEdit].value === 'string') {
          try {
              let parsedData = isStringifiedArrayWithPropName(row[colToEdit].value) ? JSON.parse(row[colToEdit].value) : [];
              
              if (Array.isArray(parsedData)) {
                  let hasMissingPropId = parsedData.some((item: any) => !item?.hasOwnProperty("propId"));
                  let hasPropId = parsedData.some((item: any) => item?.hasOwnProperty("propId"));
                  
                  if(hasPropId){
                    validDataArray.push(row)
                  }
                  if (hasMissingPropId) {
                      newArray.push(row);
                  }
              }
          } catch (error) {
              console.error("Error parsing JSON for row:", row.id, error);
          }
      }
  });
  
  return {newArray, validDataArray};
}


const findInvalidRows = (rows: any, listData: any, colToEdit: any, params: any, currentColDef: any, loggedInUser:any) => {
  return rows.filter((row: any) => {
    try {
      const parsedProps = JSON.parse(row[colToEdit].value);

      let isInvalid = false;

      parsedProps.forEach((propObj: any) => {
        const matchInList = listData.find(item =>
          item.propName === propObj.propName &&
          item.propAbbrevation === propObj.propAbbrevation &&
          item.propType === propObj.propType
        );

        if (matchInList && matchInList.propId !== propObj.propId) {
          delete propObj.propId; // Remove invalid propId
          isInvalid = true;
        }
      });

      if(row?.[colToEdit]?.value){
        let prevData = { 
            ...params.data, 
            [colToEdit]: { 
                ...params?.data?.[colToEdit], 
                value: params?.data?.[colToEdit]?.value
            } 
        };
        let myUpdatedValueForRow = updateCellValue(prevData, colToEdit, "value", JSON.stringify(parsedProps), loggedInUser);
        row[colToEdit] = myUpdatedValueForRow
        row[currentColDef.field] = myUpdatedValueForRow.value
    }
    return isInvalid;

    } catch (err) {
      console.warn("Error parsing JSON for row:", row, err);
      return true; // Consider broken JSON as invalid
    }
  });
};

type GenericObject = { [key: string]: any };

export function isArrayModified(arr1: GenericObject[], arr2: GenericObject[]): boolean {  
    if (arr1.length !== arr2.length) return true; // Different length = modified

    return arr1.some(obj1 => {
        const obj2 = arr2.find(item => item.id === obj1.id); // Compare based on unique key (modify if needed)
        if (!obj2) return true; // If an object is missing, it's modified

        return Object.keys(obj1).some(key => obj1[key] !== obj2[key]); // If any key-value pair differs
    });
}